import React, { useEffect, useRef, useState } from 'react';
import { SearchIcon, XIcon } from 'lucide-react';
import { TrackTable } from '../components/TrackTable';
import { useLibrary } from '../contexts/LibraryContext';
import { usePageMeta } from '../hooks/usePageMeta';
import { searchSuggestions } from '../data/seeds';
import { searchTracks } from '../utils/itunes';
import type { Track } from '../types/music';
export function Search() {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<Track[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const {
    recentSearches,
    rememberSearch
  } = useLibrary();
  usePageMeta({
    title: term.trim().length >= 2 ? `Search · ${term.trim()}` : 'Search the catalogue',
    description: 'Search millions of records live and play real 30-second previews instantly — artists, songs and moods, straight from the iTunes catalogue.',
    image: results[0]?.artwork,
    path: '/search'
  });
  useEffect(() => {
    const query = term.trim();
    if (query.length < 2) {
      setResults([]);
      setStatus('idle');
      return;
    }
    setStatus('loading');
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const tracks = await searchTracks(query, 25, controller.signal);
        setResults(tracks);
        setStatus('ready');
        rememberSearch(query);
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        setStatus('error');
      }
    }, 350);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [rememberSearch, term]);
  const chips = term.trim().length >= 2 ? [] : recentSearches.length ? recentSearches : searchSuggestions;
  return <div className="px-5 pb-16 pt-8 sm:px-8 lg:px-12" data-id="element-417" data-mp="1bb1js2qvh3cy">
      <h1 className="font-display text-4xl text-cream" data-id="element-418" data-mp="1fd4v8rdjk7y8">Search the catalogue</h1>
      <p className="mt-2 text-sm text-muted" data-id="element-419" data-mp="1ff99o392gpcr">Live results from the iTunes Search API as you type.</p>

      <div className="relative mt-6 max-w-xl" data-id="element-420" data-mp="3cfvn109141c4">
        <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-421" data-mp="37dwj9p9dn2en" />
        <input ref={inputRef} type="search" value={term} onChange={e => setTerm(e.target.value)} placeholder="Artists, songs, moods…" aria-label="Search for music" className="w-full rounded-full border border-ink-line bg-ink-panel py-3 pl-11 pr-11 text-sm text-cream placeholder:text-muted focus:border-gold/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-422" data-mp="3ldxrjprz1c45" />
        {term && <button type="button" onClick={() => {
        setTerm('');
        inputRef.current?.focus();
      }} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-muted transition-colors duration-150 ease-night hover:text-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-423" data-mp="2xnley1ls6r78">
            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-424" data-mp="pazrmgtjjb0k" />
          </button>}
      </div>

      {chips.length > 0 && <div className="mt-5 flex flex-wrap gap-2" data-id="element-425" data-mp="ggbmqrrvnvca">
          {chips.map(chip => <button key={chip} type="button" onClick={() => setTerm(chip)} className="rounded-full border border-ink-line px-3.5 py-1.5 text-xs text-muted transition-colors duration-150 ease-night hover:border-gold/60 hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-426" data-mp="146p5frokohu7">
              {chip}
            </button>)}
        </div>}

      <div className="mt-10" aria-live="polite" data-id="element-427" data-mp="3eww13qh6wlvw">
        {status === 'loading' && <ul className="space-y-3" data-id="element-428" data-mp="3jyd01gg45y9u">
            {Array.from({
          length: 6
        }).map((_, i) => <li key={i} className="flex items-center gap-3" data-id="element-429" data-mp="32uhiji0uhr9" data-mpi={i}>
                <span className="h-10 w-10 animate-pulse rounded bg-ink-raised" data-id="element-430" data-mp="3ugqkp6hfhe2y" />
                <span className="h-3 w-52 animate-pulse rounded bg-ink-raised" data-id="element-431" data-mp="3uenjdqatryix" />
              </li>)}
          </ul>}
        {status === 'error' && <p className="rounded-sm border border-ink-line bg-ink-panel px-4 py-3 text-sm text-cream" data-id="element-432" data-mp="3lhd5ryajbxau">
            That search didn&apos;t come back. Try again in a moment.
          </p>}
        {status === 'ready' && results.length === 0 && <p className="text-sm text-muted" data-id="element-433" data-mp="czh35ai69cuc">Nothing in the crates for “{term.trim()}”.</p>}
        {status === 'ready' && results.length > 0 && <div className="overflow-x-auto scroll-thin" data-id="element-434" data-mp="8304hmdcjg30">
            <TrackTable tracks={results} data-id="element-435" data-mp="2xqhi9dd43o9e" />
          </div>}
      </div>
    </div>;
}